import { Button } from "@mui/material";
import Papa from "papaparse";
import * as xlsx from "xlsx";
import colors from "../Constants/colors";
import { getInitCost } from "../features/chatbots/chatbotSlice";

export const mapRetrievalMethodHyperparameter = (sequence) => {
  const mapping = {
    basic: "None",
    multi_query: "Multi Phrasing",
    hyde: "HyDE",
    query_expansion: "Expansion",
    complex_to_simple: "Deconstruction",
    iterative_deconstruction: "Iterative Deconstruction",
    "user query": "User Query",
  };

  if (sequence) {
    // Check if the sequence contains the arrow symbol
    if (sequence.includes("→")) {
      return sequence
        .split("→")
        .map((item) => mapping[item.toLowerCase()] || item)
        .join(" → ");
    }

    // Return the original sequence if no arrow symbol is found
    return mapping[sequence.toLowerCase()];
  } else {
    return "N/A";
  }
};

export const mapDocumentParser = (parser) => {
  const mapping = {
    llama_parse: "Llama Parse",
    recursive_character_text_splitter: "Basic",
    unstructuredio: "Unstructured.io",
  };

  return mapping[parser] || "N/A";
};

export const formatValue = (value) => {
  if (value === 0) return value; // Return 0 as is

  const exponent = Math.floor(Math.log10(Math.abs(value)));
  const mantissa = (value / Math.pow(10, exponent)).toFixed(2); // Mantissa with 2 decimal places

  // If the exponent is between -2 and 2 (inclusive), return the rounded value with 2 decimal places
  if (exponent >= -2 && exponent <= 2) {
    return Number(value).toFixed(2);
  }

  // Otherwise, return the mantissa and exponent in scientific notation
  return (
    <>
      {mantissa} &times; 10<sup>{exponent}</sup>
    </>
  );
};

export const formattedDate = (uDate) => {
  if (!uDate) return "No Date Available"; // Handle null or undefined dates
  const date = new Date(uDate.$date || uDate); // Support both objects and direct date strings
  const fDate = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
  return fDate;
};

// helper function to check csv file header consistency
export function validateCSV(fileContent) {
  // Use Papa Parse to preview only the first two rows of the file.
  const result = Papa.parse(fileContent, {
    header: false, // Do not treat any row as header for this preview
    preview: 2, // Only process the first 2 rows
    skipEmptyLines: true,
  });

  // Ensure that there are at least 2 rows (header and one data row)
  if (result.data.length < 2) {
    return {
      error: "Not enough rows in CSV for header check",
      headerRow: [],
      firstDataRow: [],
    };
  }

  // Extract the header row and first data row
  const headerRow = result.data[0];
  const firstDataRow = result.data[1];
  const headerLength = headerRow.length;
  const firstDataRowLength = firstDataRow.length;
  const isConsistent = headerLength === firstDataRowLength;

  return {
    headerRow,
    firstDataRow,
    headerLength,
    firstDataRowLength,
    isConsistent,
  };
}

// helper function to check the values in the 3rd row (index 2) of the specified column
export const getColumnDefaultType = (file_data, columnName) => {
  if (file_data.length < 3) {
    // If the data has less than 3 rows, default to "Metadata column"
    return "Metadata column";
  }

  const value = file_data[2][columnName];
  if (value?.length > 200) {
    return "Text column";
  }
  if (!value?.includes(",")) {
    return "Metadata column"; // Not comma-separated
  }
  return value.split(",").every((part) => part.trim().split(/\s+/).length <= 4)
    ? "Metadata column"
    : "Text column";
};

export const getColumnDefaultTypeExcel = (columnData) => {
  // columnData is an array of values for this column
  if (!columnData || columnData.length < 3) {
    return "Metadata column";
  }

  // Check first few non-empty values to determine type
  const sampleSize = Math.min(5, columnData.length);
  const samples = columnData
    .filter((value) => value !== undefined && value !== null && value !== "")
    .slice(0, sampleSize);

  if (samples?.length === 0) {
    return "Metadata column";
  }

  // Helper function to check if a value looks like text content
  const isTextContent = (value) => {
    if (typeof value !== "string") {
      value = String(value);
    }

    // If length is greater than 200 characters, it's likely text content
    if (value?.length > 200) {
      return true;
    }

    // If it has multiple sentences or contains punctuation typical of text
    if (value.includes(".") && value.includes(" ")) {
      const sentences = value.split(".").filter((s) => s.trim().length > 0);
      if (sentences.length > 1) {
        return true;
      }
    }

    // If it has many words (more than 4)
    const words = value.trim().split(/\s+/);
    return words?.length > 4;
  };

  // Check if any sample looks like text content
  const hasTextContent = samples.some(isTextContent);

  // Check if values are consistently structured like metadata
  const isConsistentFormat = samples.every((value) => {
    if (typeof value !== "string") {
      value = String(value);
    }

    // Check for common metadata patterns
    const isShortValue = value?.length < 50;
    const hasNoSentences = !value.includes(".");
    const isSingleWord = value.trim().split(/\s+/).length <= 2;
    const isNumericOrDate = !isNaN(value) || !isNaN(Date.parse(value));

    return isShortValue || hasNoSentences || isSingleWord || isNumericOrDate;
  });

  // Decision logic
  if (hasTextContent && !isConsistentFormat) {
    return "Text column";
  }

  // Check for potential comma-separated metadata
  const hasCommaValues = samples.some(
    (value) => typeof value === "string" && value.includes(",")
  );

  if (hasCommaValues) {
    // Check if comma-separated values are short (metadata-like)
    const isShortCommaSeparated = samples.every((value) => {
      if (typeof value !== "string") return true;
      return value
        .split(",")
        .every((part) => part.trim().split(/\s+/).length <= 4);
    });

    return isShortCommaSeparated ? "Metadata column" : "Text column";
  }

  return "Metadata column";
};

export const parseExcelData = (file, setUploadDatasetLoading, data) => {
  try {
    // Create a workbook from the array buffer
    const workbook = xlsx.read(file, { type: "array" });

    // Iterate through each sheet name
    const sheetsData = {};
    for (const sheetName of workbook.SheetNames) {
      const worksheet = workbook.Sheets[sheetName];

      try {
        // Convert the worksheet to an array of objects
        const formatted_data = xlsx.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "", // Set default empty values for missing fields
        });
        if (formatted_data.length === 0) {
          console.warn(`Sheet ${sheetName} is empty`);
          continue;
        }

        // Extract headers from the first row
        const headers = formatted_data[0];
        const columnTypes = headers.reduce((acc, header, index) => {
          acc[header] = getColumnDefaultTypeExcel(
            formatted_data.slice(1).map((row) => row[index] || "")
          );
          return acc;
        }, {});

        // Map the file data to an array of objects using the headers
        const file_data = formatted_data.slice(1).map((row) => {
          return headers.reduce((obj, header, index) => {
            obj[header] = row[index] || ""; // Assign each header as key with its corresponding value
            return obj;
          }, {});
        });

        sheetsData[sheetName] = {
          file_data,
          headers,
          formatted_data,
          columnTypes,
        };
      } catch (sheetError) {
        console.error(`Error processing sheet ${sheetName}:`, sheetError);
        setUploadDatasetLoading(false);
      }
    }

    return sheetsData;
  } catch (error) {
    console.error("Error parsing Excel file:", error);
    data.showSnackbar(`parsing Excel file: ${error}`, "error");

    setUploadDatasetLoading(false);

    throw new Error(
      "Failed to parse Excel file. Please check if the file is valid and not corrupted."
    );
  }
};

export function getCurrentDate() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0"); // Add 1 because months are zero-based
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export const mapRetrievalSequence = (key) => {
  const keyMapping = {
    basic: "Basic",
    "sentence-window": "Chunk Window",
    context_retrieval: "Context Retrieval",
    graphrag: "GraphRag With Vector Data",
    "graphrag+basic": "GraphRag With Standard Retrieval",
    "graphrag+sentence-window": "GraphRag With Chunk Window",
    "graphrag+context_retrieval": "GraphRag With Context Retrieval",
  };

  return keyMapping[key] || key; // Fallback to the original key if no match is found
};

export const calculateInitCostAndTime = async (
  setLoading_message,
  setLoading,
  organization,
  dispatch,
  user_id,
  bot_id,
  setInitCost,
  setOpenInitConfirmDialog,
  showSnackbar,
  navigate
) => {
  setLoading_message(
    "Calculating the estimated cost for initialization. Please wait..."
  );
  setLoading(true);
  const orgId = organization?.organization?._id["$oid"];

  const calculatedCostAndTime = await dispatch(
    getInitCost({ orgId, userId: user_id, bot_id })
  );
  setInitCost(calculatedCostAndTime?.payload?.estimated_costs);
  if (calculatedCostAndTime?.payload?.status === "credits are enough") {
    setOpenInitConfirmDialog(true);
  } else {
    showSnackbar(
      "You don't have enough credits to initialize combinations. Goto 'Settings -> Usage' to purchase more credits.",
      "warning",
      <Button
        sx={{
          textTransform: "none",
          bgcolor: colors.primaryBlue,
          color: colors.extremeWhite,
          whiteSpace: "nowrap",
          "&:hover": { bgcolor: colors.secondaryBlue },
        }}
        onClick={() => navigate("/dashboard/settingsPage/usage")}
      >
        Purchase More Credits
      </Button>
    );
  }
  setLoading(false);
};

/**
 * Given a map of versionKeys → { updated_at: ISOString, … },
 * returns the key whose updated_at is newest.
 *
 * @param {Record<string, { updated_at: string }>} versions
 * @returns {string|null}
 */
export function getLatestVersionKey(versions) {
  if (!versions || typeof versions !== "object") return null;

  return (
    Object.keys(versions).sort((a, b) => {
      const da = new Date(versions[a].updated_at).getTime();
      const db = new Date(versions[b].updated_at).getTime();
      return db - da;
    })[0] || null
  );
}
